import { useQuery } from '@sequencehq/api'
import {
  Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  GreyGrey20,
  GreyGrey60,
  GreyGrey70,
  GreyGrey90,
  GreyGrey100,
  IconClipboard,
  IndigoIndigo50,
  Modal,
  Primary13Regular,
  Primary14SemiBold,
  Primary20SemiBold,
  useNotifications
} from '@sequencehq/core-components'
import { toBillingFrequencyLabel, toMoney } from '@sequencehq/utils'
import { format, MINUTES } from '@sequencehq/utils/dates'
import { inflect } from 'inflection'
import { FC, Fragment, ReactNode } from 'react'
import {
  CREDIT_TYPE_LABELS,
  GRANT_PRICE_LABEL,
  REFRESH_LABELS,
  ROLLOVER_LABELS
} from 'CreditGrants/creditGrant.constants'
import {
  getGrantPrice,
  isDiscountedGrant
} from 'CreditGrants/creditGrant.discount'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useNetsuiteItemLabel } from 'CreditGrants/hooks/useNetsuiteItemLabel'

type CreditGrantV2 = Dashboardv99990101Api.CreditGrants.CreditGrantV2

export const CreditGrantDetailDrawer: FC<{
  grant: CreditGrantV2
  onClose: () => void
}> = ({ grant, onClose }) => {
  const { displayNotification } = useNotifications()
  const { useDecoupledCreditGrantBilling } = useFeatureFlags()
  const netsuiteItemLabel = useNetsuiteItemLabel(grant)
  const { currency } = grant
  const customerQuery = useQuery(
    dashboardv99990101Client.getCustomer,
    { id: grant.customerId },
    { staleTime: 5 * MINUTES }
  )

  // Only a discounted grant has a price worth showing — otherwise it equals
  // the amount already listed above it.
  const showPrice = isDiscountedGrant(grant)
  const isAwaitingFunding = grant.status === 'AWAITING_FUNDING'
  const isActive = grant.status === 'ACTIVE'
  const used = Math.max(grant.amount - grant.remainingBalance, 0)
  const usedPercent = grant.amount > 0 ? (used / grant.amount) * 100 : 0

  const handleCopyId = () => {
    void navigator.clipboard.writeText(grant.id)
    displayNotification('Grant ID copied', {
      type: 'success',
      isClosable: true,
      duration: 2000
    })
  }

  return (
    <Modal variant="drawer" isOpen onClose={onClose}>
      <Modal.Header showCloseButton>{grant.name}</Modal.Header>
      <Modal.Content>
        <Flex
          flexDirection="column"
          gap="24px"
          data-testid="creditGrantDetail.drawer"
        >
          <Box>
            <Box {...Primary14SemiBold} color={GreyGrey100}>
              Balance
            </Box>
            <Box
              mt={4}
              color={GreyGrey100}
              {...Primary20SemiBold}
              data-testid="creditGrantDetail.balance"
            >
              {isAwaitingFunding
                ? 'Pending'
                : toMoney({ value: String(grant.remainingBalance), currency })}
            </Box>
            {isActive && (
              <>
                <Box
                  mt="12px"
                  height="6px"
                  borderRadius="3px"
                  backgroundColor={GreyGrey20}
                  overflow="hidden"
                  data-testid="creditGrantDetail.progress"
                >
                  <Box
                    width={`${usedPercent}%`}
                    height="100%"
                    backgroundColor={IndigoIndigo50}
                  />
                </Box>
                <Box mt="8px" color={GreyGrey60} {...Primary13Regular}>
                  {toMoney({ value: String(used), currency })} used out of{' '}
                  {toMoney({ value: String(grant.amount), currency })}
                </Box>
              </>
            )}
          </Box>

          <Box>
            <Box {...Primary14SemiBold} color={GreyGrey100}>
              Details
            </Box>
            <Grid
              templateColumns="160px 1fr"
              gap={3}
              mt={4}
              color={GreyGrey70}
              {...Primary13Regular}
            >
              <DetailRow label="Customer">
                {customerQuery.data?.legalName ?? '—'}
              </DetailRow>
              <DetailRow label="Name">{grant.name}</DetailRow>
              <DetailRow label="Amount" testId="creditGrantDetail.amount">
                {toMoney({ value: String(grant.amount), currency })}
              </DetailRow>
              {showPrice && (
                <DetailRow
                  label={GRANT_PRICE_LABEL}
                  testId="creditGrantDetail.price"
                >
                  {toMoney({ value: String(getGrantPrice(grant)), currency })}
                </DetailRow>
              )}
              {/* While coupled, one row is accurate under either name. */}
              <DetailRow
                label={
                  useDecoupledCreditGrantBilling
                    ? 'Refresh frequency'
                    : 'Billing frequency'
                }
              >
                {REFRESH_LABELS[grant.grantRefreshFrequency]}
              </DetailRow>
              {useDecoupledCreditGrantBilling && (
                <DetailRow label="Billing frequency">
                  {toBillingFrequencyLabel(grant.billingFrequency)}
                </DetailRow>
              )}
              <DetailRow label="Type">
                {CREDIT_TYPE_LABELS[grant.creditUnitType]}
              </DetailRow>
              <DetailRow label="Issued">
                {format(new Date(grant.createdAt), 'd MMM yyyy')}
              </DetailRow>
              <DetailRow label="Expires">
                {grant.expiryDate
                  ? format(new Date(grant.expiryDate), 'd MMM yyyy')
                  : 'No expiry'}
              </DetailRow>
              <DetailRow label="Roll-over">
                {ROLLOVER_LABELS[grant.rolloverMode]}
              </DetailRow>
              <DetailRow label="Applied to">
                {grant.priceIds.length
                  ? `${grant.priceIds.length} ${inflect(
                      'product',
                      grant.priceIds.length
                    )}`
                  : 'All products'}
              </DetailRow>

              {netsuiteItemLabel && (
                <DetailRow
                  label="NetSuite item"
                  testId="creditGrantDetail.netsuiteItem"
                >
                  {netsuiteItemLabel}
                </DetailRow>
              )}

              <Box>Grant ID</Box>
              <Flex gap={2} alignItems="center" minWidth={0}>
                <Badge sentiment="neutral" truncate>
                  {grant.id}
                </Badge>
                <Box flexShrink={0} display="flex">
                  <Button
                    variant="ghost"
                    size="24px"
                    onClick={handleCopyId}
                    aria-label="Copy grant ID"
                    data-testid="creditGrantDetail.copyId"
                  >
                    <IconClipboard size={14} color={GreyGrey60} />
                  </Button>
                </Box>
              </Flex>
            </Grid>
          </Box>
        </Flex>
      </Modal.Content>
    </Modal>
  )
}

const DetailRow: FC<{
  label: string
  children: ReactNode
  testId?: string
}> = ({ label, children, testId }) => (
  <Fragment>
    <Box>{label}</Box>
    <Box color={GreyGrey90} data-testid={testId}>
      {children}
    </Box>
  </Fragment>
)
